import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TickerWithName from "../components/TickerWithName";
import api from "../utils/api";

// helpers
const money2 = (v) => (v == null || v === '' ? '0.00' : (Number.isFinite(Number(v)) ? Number(v).toFixed(2) : String(v)));
const pctChange = (a, b) => {
    const x = Number(a), y = Number(b);
    if (!Number.isFinite(x) || !Number.isFinite(y) || x === 0) return null;
    return ((y - x) / x) * 100;
};
const EstExitLine = ({ entry, exit }) => {
    const pct = pctChange(entry, exit);
    const good = pct !== null && pct >= 0;
    return (
        <div className="text-sm">
            Est Exit: ${money2(exit)}{" "}
            {pct !== null && (
                <span className={`ml-1 px-2 py-0.5 rounded text-xs font-semibold ${good ? "bg-green-600" : "bg-red-600"}`}>
                    {good ? "+" : ""}{pct.toFixed(2)}%
                </span>
            )}
        </div>
    );
};

const CryptoPage = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // server paging/sorting
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [limit, setLimit] = useState(50);
    const [sort, setSort] = useState('score');
    const [order, setOrder] = useState('desc');

    const navigate = useNavigate();

    useEffect(() => {
        let cancel = false;
        (async () => {
            try {
                setLoading(true);
                setError('');
                const { data } = await api.get(
                    '/api/crypto-trades-live',
                    { params: { page, limit, sort, order }, timeout: 20000 }
                );
                if (cancel) return;
                if (data && Array.isArray(data.results)) {
                    setRows(data.results);
                    setPages(data.pages || 1);
                } else {
                    setRows([]);
                    setPages(1);
                }
            } catch (e) {
                if (!cancel) setError('Network error. Please try again.');
                console.error('crypto fetch error', e);
            } finally {
                if (!cancel) setLoading(false);
            }
        })();
        return () => { cancel = true; };
    }, [page, limit, sort, order]);

    return (
        <div className="p-4 sm:p-5">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-blue-400 drop-shadow">🪙 Top Crypto Trades</h2>

            {/* Controls */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <label className="text-sm text-gray-300">
                    Show
                    <select className="ml-2 bg-gray-800 rounded px-2 py-1" value={limit}
                        onChange={(e) => { setPage(1); setLimit(parseInt(e.target.value, 10)); }}>
                        {[25, 50].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                    / page
                </label>

                <label className="text-sm text-gray-300">
                    Sort by
                    <select className="ml-2 bg-gray-800 rounded px-2 py-1" value={sort}
                        onChange={(e) => { setPage(1); setSort(e.target.value); }}>
                        <option value="score">Score</option>
                        <option value="ticker">Ticker</option>
                        <option value="entryPrice">Entry</option>
                        <option value="estimatedExit">Est Exit</option>
                        <option value="confidence">Confidence</option>
                    </select>
                </label>

                <label className="text-sm text-gray-300">
                    Order
                    <select className="ml-2 bg-gray-800 rounded px-2 py-1" value={order}
                        onChange={(e) => { setPage(1); setOrder(e.target.value); }}>
                        <option value="desc">Desc</option>
                        <option value="asc">Asc</option>
                    </select>
                </label>
            </div>

            {loading && <div className="text-gray-400">Loading…</div>}
            {error && <div className="text-red-400">{error}</div>}

            <div className="space-y-3">
                {rows.map((r, i) => (
                    <div
                        key={i}
                        className="bg-gray-700 p-3 sm:p-4 rounded-lg shadow hover:bg-gray-600 cursor-pointer space-y-1"
                        onClick={() => r.ticker && navigate(`/chart/${r.ticker}`)}
                    >
                        <div className="flex items-center text-lg sm:text-xl font-semibold">
                            <TickerWithName symbol={r.ticker} />
                        </div>
                        {r.score !== undefined && <div className="text-sm text-gray-300">Score: {r.score}</div>}
                        {r.entryPrice !== undefined && <div className="text-sm">Entry: ${money2(r.entryPrice)}</div>}
                        {r.estimatedExit !== undefined && <EstExitLine entry={r.entryPrice} exit={r.estimatedExit} />}
                        {r.confidence !== undefined && <div className="text-sm text-blue-300">Confidence: {r.confidence}%</div>}
                        {r.reason && <div className="text-sm text-gray-400">Reason: {r.reason}</div>}
                    </div>
                ))}
            </div>

            {pages > 1 && (
                <div className="flex justify-center items-center space-x-4 mt-6">
                    <button disabled={page === 1}
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        className="px-3 py-1 bg-gray-700 rounded disabled:opacity-50 hover:bg-blue-500">
                        ⬅ Prev
                    </button>
                    <span className="text-gray-300">Page {page} of {pages}</span>
                    <button disabled={page === pages}
                        onClick={() => setPage(p => Math.min(pages, p + 1))}
                        className="px-3 py-1 bg-gray-700 rounded disabled:opacity-50 hover:bg-blue-500">
                        Next ➡
                    </button>
                </div>
            )}
        </div>
    );
};

export default CryptoPage;
